
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

export type Subscription = {
  id: string;
  status: string;
  date_debut: string;
  date_fin: string;
  montant_total: number;
  stripe_subscription_id: string;
  stripe_customer_id: string;
  stripe_plan_id: string;
  niveau_compte?: {
    id: number;
    niveau: string;
    prix: number;
    description: string;
  };
};

export const useCurrentSubscription = () => {
  const { user, userProfile } = useAuth();

  // Fetch the current subscription for the authenticated user
  return useQuery({
    queryKey: ['subscription', user?.id],
    queryFn: async () => {
      try {
        // Check if user is authenticated and is a freemium user
        if (!user) {
          console.log("No authenticated user found");
          return null;
        }

        if (userProfile?.niveau_compte_id === 1) {
          console.log("Compte freemium détecté, pas besoin de chercher un abonnement Stripe");
          return {
            id: 'freemium',
            status: 'active',
            date_debut: new Date().toISOString(),
            date_fin: null,
            montant_total: 0,
            stripe_subscription_id: null,
            stripe_customer_id: null,
            stripe_plan_id: null,
            niveau_compte: {
              id: 1,
              niveau: 'freemium',
              prix: 0,
              description: 'Niveau gratuit avec fonctionnalités limitées'
            }
          };
        }

        return await fetchSubscription(user.id);
      } catch (err) {
        console.error("Exception dans useCurrentSubscription:", err);
        return null;
      }
    },
    enabled: !!user,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

// Function to fetch subscription from Supabase
async function fetchSubscription(userId: string): Promise<Subscription | null> {
  try {
    console.log("Fetching subscription for user:", userId);
    
    // First, let's try to get the active subscription using the correct relationship
    const { data: subscriptionData, error: subscriptionError } = await supabase
      .from('subscriptions')
      .select(`
        id,
        status,
        date_debut,
        date_fin,
        montant_total,
        stripe_subscription_id,
        stripe_customer_id,
        stripe_plan_id,
        niveau_compte_id
      `)
      .eq('user_id', userId)
      .eq('status', 'active')
      .order('date_debut', { ascending: false })
      .limit(1)
      .single();

    if (subscriptionError) {
      console.error("Erreur lors de la récupération de l'abonnement:", subscriptionError);
      
      // Check if this is just a "no rows returned" error
      if (subscriptionError.code === 'PGRST116') {
        console.log("Aucun abonnement actif trouvé pour l'utilisateur");
        return null;
      }
      
      throw new Error(subscriptionError.message);
    }

    if (!subscriptionData) {
      console.log("Aucun abonnement trouvé pour l'utilisateur");
      return null;
    }

    console.log("Abonnement trouvé:", subscriptionData);
    
    // Now get the niveau_compte separately
    const { data: niveauCompte, error: niveauError } = await supabase
      .from('niveaux_comptes')
      .select('id, niveau, prix, description')
      .eq('id', subscriptionData.niveau_compte_id)
      .single();

    if (niveauError) {
      console.error("Erreur lors de la récupération du niveau de compte:", niveauError);
    }

    // Return combined data
    return {
      ...subscriptionData,
      niveau_compte: niveauCompte || {
        id: subscriptionData.niveau_compte_id,
        niveau: 'unknown',
        prix: 0,
        description: 'Niveau inconnu'
      }
    };
  } catch (err) {
    console.error("Exception dans fetchSubscription:", err);
    throw err;
  }
}
